import { NRelay1 } from '@nostrify/nostrify';
import { useQuery } from '@tanstack/react-query';

interface RelayMetadata {
  read?: boolean;
  write?: boolean;
}

export interface UserRelays {
  relays: Map<string, RelayMetadata>;
  readRelays: string[];
  writeRelays: string[];
  allRelays: string[];
}

// Default relays to check for user's kind 3 event
const BOOTSTRAP_RELAYS = [
  'wss://relay.damus.io',
  'wss://relay.nostr.band',
  'wss://relay.primal.net',
  'wss://nos.lol',
];

/**
 * Fetch a user's relay list from their kind 3 contact list event.
 * Uses bootstrap relays to avoid circular dependency with NostrProvider.
 */
export function useUserRelays(pubkey: string | undefined) {
  return useQuery<UserRelays>({
    queryKey: ['user-relays', pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!pubkey) {
        return {
          relays: new Map(),
          readRelays: [],
          writeRelays: [],
          allRelays: [],
        };
      }

      // Try bootstrap relays to find the user's kind 3 event
      let event;
      for (const relayUrl of BOOTSTRAP_RELAYS) {
        try {
          const relay = new NRelay1(relayUrl);
          await relay.connect();

          const events = await relay.query(
            [{ kinds: [3], authors: [pubkey], limit: 1 }],
            { signal: AbortSignal.any([signal, AbortSignal.timeout(1500)]) },
          );

          relay.close();

          if (events.length > 0) {
            event = events[0];
            break;
          }
        } catch (error) {
          // Try next relay
          continue;
        }
      }

      if (!event) {
        return {
          relays: new Map(),
          readRelays: [],
          writeRelays: [],
          allRelays: [],
        };
      }

      // Parse the relay list from the content field
      const relays = new Map<string, RelayMetadata>();
      const readRelays: string[] = [];
      const writeRelays: string[] = [];
      const allRelays: string[] = [];

      try {
        const content = JSON.parse(event.content);

        for (const [url, metadata] of Object.entries(content)) {
          // Normalize relay URL
          const normalizedUrl = url.trim();

          if (!normalizedUrl.startsWith('wss://') && !normalizedUrl.startsWith('ws://')) {
            continue;
          }

          const relayMetadata = metadata as RelayMetadata;
          relays.set(normalizedUrl, relayMetadata);
          allRelays.push(normalizedUrl);

          // Default to read+write if not specified
          const canRead = relayMetadata.read !== false;
          const canWrite = relayMetadata.write !== false;

          if (canRead) {
            readRelays.push(normalizedUrl);
          }
          if (canWrite) {
            writeRelays.push(normalizedUrl);
          }
        }
      } catch (error) {
        console.warn('Failed to parse relay list from kind 3 event:', error);
      }

      return {
        relays,
        readRelays,
        writeRelays,
        allRelays,
      };
    },
    enabled: !!pubkey,
    staleTime: 5 * 60 * 1000, // Keep cached for 5 minutes
    retry: 2,
  });
}
